import { useRef, useMemo, useState } from 'react';
import { Badge } from '@/core/components/ui/badge';
import { Button } from '@/core/components/ui/button';
import { Textarea } from '@/core/components/ui/textarea';
import { Label } from '@/core/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/core/components/ui/tooltip';
import { PriceTextPlaceholder } from '../../types/manufacturer-types';
import { Eye, Sparkles, HelpCircle } from 'lucide-react';

interface TemplateFieldWithHelperProps {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  placeholders: PriceTextPlaceholder[];
  exampleTemplates: string[];
  placeholder?: string;
  error?: string;
  maxLength?: number;
}

// Fallback sample values if API doesn't provide example
const FALLBACK_SAMPLE_VALUES: Record<string, string> = {
  '{p}': '0.30',
  '{f}': '2.50',
  '{t}': '1.00',
  '{i}': '0.50',
  '{pv}': '0.35',
  '{fv}': '2.93',
  '{tv}': '1.17',
  '{iv}': '0.59',
  '{c}': 'EUR',
  '{totalCost}': '3.31',
  '{energyCost}': '2.81',
  '{timeCost}': '0.50',
  '{idleCost}': '0.25',
  '{flatCost}': '0.50',
  '{totalKwh}': '23.4',
  '{duration}': '03:50:00',
  '{idleMinutes}': '15',
};

export function TemplateFieldWithHelper({
  label,
  description,
  value,
  onChange,
  placeholders,
  exampleTemplates,
  placeholder = 'e.g., {pv}{c}/kWh',
  error,
  maxLength = 500,
}: TemplateFieldWithHelperProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedExample, setSelectedExample] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  // Build sample values map from API placeholders (with fallback)
  const sampleValues = useMemo(() => {
    const values: Record<string, string> = { ...FALLBACK_SAMPLE_VALUES };
    placeholders.forEach((p) => {
      const ph = p.key.startsWith('{') ? p.key : `{${p.key}}`;
      if (p.example) {
        values[ph] = p.example;
      }
    });
    return values;
  }, [placeholders]);

  // Generate preview by replacing placeholders with sample values
  const preview = useMemo(() => {
    if (!value) return '';

    let result = value;
    Object.entries(sampleValues).forEach(([ph, val]) => {
      result = result.replace(new RegExp(ph.replace(/[{}]/g, '\\$&'), 'g'), val);
    });
    result = result.replace(/\{([^}]+)\}/g, '[$1]');

    return result;
  }, [value, sampleValues]);

  const insertPlaceholder = (ph: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onChange(value + ph);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = value.substring(0, start) + ph + value.substring(end);
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + ph.length, start + ph.length);
    }, 0);
  };

  const handleUseExample = () => {
    if (selectedExample) {
      onChange(selectedExample);
      setSelectedExample('');
    }
  };

  return (
    <div className={`rounded-lg border p-4 transition-all ${isFocused ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white'}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <Label className="text-sm font-semibold text-gray-900">{label}</Label>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="text-gray-400 hover:text-gray-600">
                <HelpCircle className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">
              <p className="text-xs">Use placeholders like {'{pv}'} to insert dynamic pricing values. Click a placeholder below to add it.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Placeholders - Compact inline */}
      {placeholders.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5 font-medium">
            Click to insert:
          </p>
          <div className="flex flex-wrap gap-1">
            {placeholders.map((p) => {
              const ph = p.key.startsWith('{') ? p.key : `{${p.key}}`;
              const exampleVal = p.example || sampleValues[ph];
              return (
                <TooltipProvider key={p.key} delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="cursor-pointer hover:bg-primary hover:text-white transition-all text-[11px] font-mono px-1.5 py-0.5"
                        onClick={() => insertPlaceholder(ph)}
                      >
                        {ph}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[250px]">
                      <p className="font-medium text-xs">{p.description}</p>
                      {exampleVal && <p className="text-[10px] text-blue-300">Example: {exampleVal}</p>}
                      {p.source && <p className="text-[10px] text-gray-400 mt-1">Source: {p.source}</p>}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        </div>
      )}

      {/* Textarea */}
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        className={`min-h-[60px] font-mono text-sm resize-none ${error ? 'border-red-500' : ''}`}
        maxLength={maxLength}
      />

      <div className="flex items-center justify-between mt-1.5">
        <p className="text-[10px] text-gray-400">{value.length}/{maxLength}</p>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>

      {/* Live Preview */}
      {value && (
        <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded flex items-start gap-2">
          <Eye className="h-3.5 w-3.5 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-green-700 font-medium">Preview</p>
            <p className="text-sm font-medium text-green-900 break-words">{preview}</p>
          </div>
        </div>
      )}

      {/* Example Templates */}
      {exampleTemplates.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Select value={selectedExample} onValueChange={setSelectedExample}>
              <SelectTrigger className="flex-1 h-8 text-xs">
                <SelectValue placeholder="Or pick an example..." />
              </SelectTrigger>
              <SelectContent>
                {exampleTemplates.map((template, index) => (
                  <SelectItem key={index} value={template} className="text-xs font-mono">
                    {template}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleUseExample}
              disabled={!selectedExample}
              className="h-8 px-2"
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Use
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
